import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { getSubscriptionDetails, createPortalSession } from '../services/api';

const Subscription = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSubscriptionDetails();
      setSubscriptionData(response.data);
    } catch (err) {
      console.error('Error fetching subscription details:', err);
      setError(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Failed to load subscription details. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true);
      setError(null);
      const response = await createPortalSession();
      if (response.data && response.data.portal_url) {
        // Redirect to Stripe billing portal
        window.location.href = response.data.portal_url;
      } else {
        setError('Failed to create portal session. Please try again.');
        setPortalLoading(false);
      }
    } catch (err) {
      console.error('Error creating portal session:', err);
      setError(
        err.response?.data?.detail ||
        'Failed to open subscription management. Please try again.'
      );
      setPortalLoading(false);
    }
  };

  const formatAmount = (amount, currency = 'USD') => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100); // Convert cents to dollars
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Subscription Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card elevation={3}>
        <CardContent sx={{ p: 4 }}>
          {!subscriptionData || !subscriptionData.has_active_subscription ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CancelIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                No Active Subscription
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                You don't have an active subscription. Subscribe to unlock premium features.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/pricing')}
                sx={{ minWidth: 200 }}
              >
                View Plans
              </Button>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Active"
                  color="success"
                  size="small"
                  sx={{ mb: 2 }}
                />
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                  {subscriptionData.plan_name}
                </Typography>
                {subscriptionData.amount && (
                  <Typography variant="h6" color="text.secondary">
                    {formatAmount(subscriptionData.amount, subscriptionData.currency)}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  startIcon={portalLoading ? <CircularProgress size={20} /> : <CreditCardIcon />}
                  sx={{ minWidth: 250, py: 1.5 }}
                >
                  {portalLoading ? 'Loading...' : 'Manage Subscription'}
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Manage your subscription, update payment method, or cancel in Stripe's secure billing portal.
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default Subscription;
